import { useOperatorContext } from '../app/components/BaseLayoutAndData.tsx';
import { useEffect, useState } from 'react';
import { Box, Stack } from '@mui/material';
import { Outlet } from 'react-router-dom';
import { GroupSegment } from '../shared-mui-components/GroupSegment.tsx';
import { CloudConnectorPeripheryType } from 'dg-web-shared/model/CloudConnector.ts';
import {
    OperatorPeripheriesList,
    OperatorZoneBarrierGatePeriphery,
    PeripheryDisplay,
    useZoneBarrierGatePeripheriesFetch,
} from './CloudConnectorPeripheriesOverview.tsx';
import { OperatorAsyncLoadedSection } from '../app/components/OperatorAsyncLoadedSection.tsx';
import { RequestStatus } from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';

export function PeripheriesOverviewFullscreen() {
    const { currentLogin } = useOperatorContext();
    const [refetchCounter, setRefetchCounter] = useState(0);
    const [peripheriesListRequestState] = useZoneBarrierGatePeripheriesFetch(
        currentLogin.mandantId,
        refetchCounter,
    );

    useEffect(() => {
        if (
            peripheriesListRequestState.status === RequestStatus.SUCCESS ||
            peripheriesListRequestState.status === RequestStatus.ERROR
        ) {
            const timeout = setTimeout(() => {
                setRefetchCounter(prevCount => prevCount + 1);
            }, 5000);
            return () => clearTimeout(timeout);
        }
        return undefined;
    }, [peripheriesListRequestState.status, refetchCounter]);

    return (
        <OperatorAsyncLoadedSection
            requestState={peripheriesListRequestState}
            render={peripheriesList => (
                <Stack
                    direction={'row'}
                    sx={{ paddingLeft: '24px', justifyContent: 'center' }}
                >
                    {peripheriesList.map(parking => (
                        <FullscreenParkingSegment
                            parkingData={parking}
                            key={parking.zoneId}
                        />
                    ))}
                    <Outlet />
                </Stack>
            )}
            spinnerFreeRefresh
        />
    );
}

function FullscreenParkingSegment({
    parkingData,
}: {
    parkingData: OperatorPeripheriesList;
}) {
    return (
        <GroupSegment label={parkingData.name} direction={'row'}>
            <Stack direction={'row'} spacing={1} sx={{ width: '592px' }}>
                <Box>
                    <FullscreenPeripheryColumnSection
                        configs={parkingData.peripheryConfigs.filter(
                            peripheryConfig =>
                                peripheryConfig.peripheryType ===
                                CloudConnectorPeripheryType.ENTRY,
                        )}
                    />
                    <FullscreenPeripheryColumnSection
                        configs={parkingData.peripheryConfigs.filter(
                            peripheryConfig =>
                                peripheryConfig.peripheryType ===
                                CloudConnectorPeripheryType.EXIT,
                        )}
                    />
                </Box>
                <Box>
                    <FullscreenPeripheryColumnSection
                        configs={parkingData.peripheryConfigs.filter(
                            peripheryConfig =>
                                peripheryConfig.peripheryType ===
                                CloudConnectorPeripheryType.PAY_STATION,
                        )}
                    />
                    <FullscreenPeripheryColumnSection
                        configs={parkingData.peripheryConfigs.filter(
                            peripheryConfig =>
                                [
                                    CloudConnectorPeripheryType.ACCESS,
                                    CloudConnectorPeripheryType.TRANSIT_ENTRY,
                                ].includes(peripheryConfig.peripheryType),
                        )}
                    />
                </Box>
            </Stack>
        </GroupSegment>
    );
}

function FullscreenPeripheryColumnSection({
    configs,
}: {
    configs: OperatorZoneBarrierGatePeriphery[];
}) {
    if (configs.length === 0) return null;

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                marginTop: '0px !important',
                marginLeft: '0px !important',
                width: '50%',
                paddingTop: '24px',
                '&:nth-child(1)': { paddingTop: '0px' },
            }}
        >
            {configs.map(peripheryConfig => (
                <PeripheryDisplay
                    key={`${peripheryConfig.onPremiseId}-${peripheryConfig.peripheryType}`}
                    peripheryConfig={peripheryConfig}
                    playAlertSound={true}
                />
            ))}
        </Box>
    );
}
